import { useCallback, useMemo } from 'react';

import { RootStore, useTypedSelector } from 'store';
import {
  EArbitrageFilters,
  ECexPairFilters,
  EDexPairFilters,
  EGeneralFilters,
  EProjectFilters,
  ESolPairFilters,
} from 'types/filters';
import { IFilterValue } from 'types/filters/common';
import { ICexPair, IDexPair } from 'types/pairs';

import { getProjectFiltersKey } from '../utils';

const EMPTY_FILTERS: IFilterValue[] = [];

interface IProps {
  general?: EGeneralFilters;
  arbitrage?: EArbitrageFilters;
  cex?: {
    pair?: ICexPair;
    type: ECexPairFilters;
  };
  dex?: {
    pair?: IDexPair;
    type: EDexPairFilters;
  };
  sol?: {
    pair?: IDexPair;
    type: ESolPairFilters;
  };
  project?: {
    projectId?: number;
    type: EProjectFilters;
  };
}

export function useIsFiltersSet({ general, arbitrage, cex, dex, sol, project }: IProps) {
  const filtersSelector = useCallback(
    (store: RootStore) => {
      if (general) {
        return store.filters[general]?.setted ?? EMPTY_FILTERS;
      }

      if (arbitrage) {
        return store.filters.arbitrage[arbitrage]?.setted ?? EMPTY_FILTERS;
      }

      if (cex) {
        return store.filters.cex[cex.pair?.id ?? 0]?.filters?.[cex.type]?.setted ?? EMPTY_FILTERS;
      }

      if (dex) {
        return store.filters.dex[dex.pair?.id ?? 0]?.filters?.[dex.type]?.setted ?? EMPTY_FILTERS;
      }

      if (sol) {
        return store.filters.sol[sol.pair?.id ?? 0]?.filters?.[sol.type]?.setted ?? EMPTY_FILTERS;
      }

      if (project?.projectId) {
        const key = getProjectFiltersKey(project.projectId, project.type);
        return store.filters.project[key]?.setted ?? EMPTY_FILTERS;
      }

      return EMPTY_FILTERS;
    },
    [general, arbitrage, cex, dex, sol, project],
  );

  const filters = useTypedSelector(filtersSelector);

  const isSetted = useMemo(() => {
    if (!filters) return false;

    for (const field of filters) {
      if (field.value !== undefined) return true;
    }

    return false;
  }, [filters]);

  return isSetted;
}
